import router from "@/router";
import { userModule } from "@/store";
import { ROUTES } from "@/utils/enums";

import { toUtmQueryParams } from "./attribution";

/**
 * Real SPA routes for Ads/GTM. Conversions key off browser `url` (History Change),
 * not dataLayer-only `content-view`. Ads should trigger on these billing plan-change
 * result paths — not on `/billing/plan-and-subscription` itself.
 *
 * Paths live under `billing/subscription-result/` (not under
 * `billing/plan-and-subscription/`) so existing "contains"/"starts with" triggers on the
 * plan page cannot cross-match these conversion URLs.
 */
export const BILLING_PLAN_CHANGE_PAGES = {
  upgradeSuccess: ROUTES.TEAM_BILLING_UPGRADE_SUCCESS,
  downgradeSuccess: ROUTES.TEAM_BILLING_DOWNGRADE_SUCCESS,
} as const;

export type BillingPlanChangePage = keyof typeof BILLING_PLAN_CHANGE_PAGES;

/**
 * Time the virtual URL stays in the address bar before restoring the base route.
 * Long enough for GTM History Change listeners and Ads pixels to read the URL,
 * short enough that users are unlikely to refresh/bookmark/share it.
 */
const VIRTUAL_PAGE_DWELL_MS = 1000;

export function trackBillingPlanChangePage(page: BillingPlanChangePage): void {
  const routeName = BILLING_PLAN_CHANGE_PAGES[page];
  const { params, query } = router.currentRoute.value;

  // Carry utm_* as query params on the virtual page URL, so GTM's History
  // Change trigger reads them straight off the URL — no dataLayer event
  // needed. Prefers this session's touch, falling back to the server-sourced
  // one so a conversion in a browser that never saw the ad click still works.
  const utmParams = toUtmQueryParams(userModule.userProfileGetter?.attribution);

  const navigate = async () => {
    try {
      // utmParams spread last: a stored/captured touch is intentionally
      // preferred over whatever utm_* the current route's live query already
      // carries, since the current query can be stale (e.g. a plan-change
      // flow started from a page visited long before this touch was captured).
      await router.replace({ name: routeName, params, query: { ...query, ...utmParams } });
      setTimeout(() => {
        // Restore the base route so refreshes/bookmarks don't re-fire conversions.
        // Skip if the user has already navigated elsewhere in the meantime.
        if (router.currentRoute.value.name === routeName) {
          void router.replace({ name: ROUTES.TEAM_BILLING_PLAN_AND_SUBSCRIPTION, params, query });
        }
      }, VIRTUAL_PAGE_DWELL_MS);
    } catch {
      // Tracking navigation must never break the billing flow.
    }
  };
  void navigate();
}
